/**
 * Admin → Contract Setup
 * Single home for all the lookups the Contract Pack wizard relies on:
 *   - Hardware Presets (quick-add line items)
 *   - Manufacturer Warranty Defaults (auto-fill warranty fields by mfr)
 *   - Battery Presets (MIS-3012 spec table per battery model)
 *
 * Tab is selected via the `?tab=` query string so links from the wizard /
 * sidebar can deep-link to a specific section (e.g. /admin/contract-setup?tab=batteries).
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Package, ShieldCheck, Battery, Settings, Building2, PoundSterling } from 'lucide-react';
import { Layout } from '../components/Layout';

import { HardwarePresetsContent } from './HardwarePresets';
import { ManufacturerWarrantiesContent } from './ManufacturerWarranties';
import { BatteryPresetsContent } from './BatteryPresets';
import { CompanyAccreditationsContent } from './CompanyAccreditations';
import { ContractPricingContent } from './ContractPricingRates';

const VALID_TABS = ['hardware', 'warranties', 'batteries', 'company', 'pricing'];

const ContractSetup = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('tab');
  const active = VALID_TABS.includes(initial) ? initial : 'hardware';

  const setTab = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="contract-setup-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-slate-600" />
            Contract Setup
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage the shared lookups the Contract Pack wizard uses — quick-add hardware items,
            manufacturer warranty defaults, and per-battery MIS-3012 spec tables.
          </p>
        </div>

        <Tabs value={active} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-5 w-full md:w-[900px] h-10">
            <TabsTrigger value="hardware" data-testid="tab-hardware" className="gap-2 data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700">
              <Package className="w-4 h-4" />
              Hardware Presets
            </TabsTrigger>
            <TabsTrigger value="warranties" data-testid="tab-warranties" className="gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
              <ShieldCheck className="w-4 h-4" />
              Mfr Warranties
            </TabsTrigger>
            <TabsTrigger value="batteries" data-testid="tab-batteries" className="gap-2 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700">
              <Battery className="w-4 h-4" />
              Battery Presets
            </TabsTrigger>
            <TabsTrigger value="company" data-testid="tab-company" className="gap-2 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700">
              <Building2 className="w-4 h-4" />
              Company &amp; Accreditations
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing" className="gap-2 data-[state=active]:bg-rose-50 data-[state=active]:text-rose-700">
              <PoundSterling className="w-4 h-4" />
              Pricing Rates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hardware" className="mt-6">
            <HardwarePresetsContent embedded />
          </TabsContent>
          <TabsContent value="warranties" className="mt-6">
            <ManufacturerWarrantiesContent embedded />
          </TabsContent>
          <TabsContent value="batteries" className="mt-6">
            <BatteryPresetsContent embedded />
          </TabsContent>
          <TabsContent value="company" className="mt-6">
            <CompanyAccreditationsContent embedded />
          </TabsContent>
          <TabsContent value="pricing" className="mt-6">
            <ContractPricingContent embedded />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ContractSetup;
